import { z } from 'zod';
import type { Timeframe, SubscriptionPlan, Candle, AIModel } from '../types/index.js';
import { TIMEFRAMES, SUBSCRIPTION_PLANS, DEFAULT_AI_MODEL, TRADE_LIMITS } from '../constants/index.js';

// ============================================================================
// Validation Schemas
// ============================================================================

export const emailSchema = z.string().email('validation.email.invalid');

export const passwordSchema = z
  .string()
  .min(8, 'validation.password.minLength')
  .regex(/[A-Z]/, 'validation.password.uppercase')
  .regex(/[a-z]/, 'validation.password.lowercase')
  .regex(/[0-9]/, 'validation.password.number');

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'validation.password.required'),
});

export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: z.string().min(2, 'validation.name.minLength').max(100),
  language: z.enum(['en', 'pt-BR', 'es']).optional(),
});

export const timeframeSchema = z.enum(['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w']);

export const aiModelEnum = z.enum([
  'gemini-2.5-flash',
  'gemini-2.5-pro',
  'openai/gpt-4o',
  'openai/gpt-4o-mini',
  'anthropic/claude-sonnet-4',
  'deepseek/deepseek-chat',
  'google/gemini-2.5-flash',
]);

// Soft-fallback variant used at the analysis request boundary. A stale
// localStorage from a prior build (e.g. "gemini-3-flash-preview") must NOT
// fail the user's analysis with VAL_001 — silently coerce to DEFAULT and let
// the route handler log the fallback for internal diagnostics.
export const aiModelSchema = aiModelEnum
  .catch(DEFAULT_AI_MODEL) as unknown as z.ZodType<AIModel>;

// Single source of truth — shared between schema, frontend pre-checks, and
// backend error responses (so the user always sees the real bound).
export const ANALYSIS_CANDLE_MIN = 50;
export const ANALYSIS_CANDLE_MAX = 500;

export const analysisRequestSchema = z.object({
  symbol: z.string().min(1),
  timeframe: timeframeSchema,
  methodology: z.enum(['smc', 'ict', 'price-action', 'fibonacci']).default('smc'),
  aiModel: aiModelSchema.default(DEFAULT_AI_MODEL),
  candles: z.array(z.object({
    time: z.number(),
    open: z.number(),
    high: z.number(),
    low: z.number(),
    close: z.number(),
    volume: z.number().optional(),
  })).min(ANALYSIS_CANDLE_MIN).max(ANALYSIS_CANDLE_MAX),
  models: z.array(z.string()).min(1).max(5).optional(),
  selectionStrategy: z.enum(['fastest', 'best']).optional(),
  // Optional custom prompt addendum to append to the official methodology
  // prompt. The API ignores it for non-Pro+ plans (defense-in-depth — the
  // frontend already gates the picker).
  userPromptId: z.string().uuid().optional(),
});

// ---------------------------------------------------------------------------
// MethodologyOverlay Zod schema
//
// Optional discriminated union mirroring the TypeScript `MethodologyOverlay`
// shape from `../types/index.ts`. Used by the worker to defensively parse AI
// responses that include the new field — older models that don't emit it
// simply produce `undefined`, preserving legacy SMC-only behaviour.
// ---------------------------------------------------------------------------

const _zoneSchema = z.object({
  type: z.enum(['bullish', 'bearish']),
  top: z.number(),
  bottom: z.number(),
  strength: z.number(),
  status: z.enum(['fresh', 'tested', 'mitigated']),
  time: z.number(),
  timeEnd: z.number().optional(),
});

const _fvgSchema = z.object({
  type: z.enum(['bullish', 'bearish']),
  top: z.number(),
  bottom: z.number(),
  filledPercent: z.number(),
  time: z.number(),
});

const _liquiditySchema = z.object({
  type: z.enum(['bsl', 'ssl']),
  price: z.number(),
  strength: z.number(),
  time: z.number(),
});

const _structurePointSchema = z.object({
  type: z.enum(['bullish', 'bearish']),
  price: z.number(),
  time: z.number(),
});

const _candlePatternSchema = z.object({
  pattern: z.enum(['pin-bar', 'engulfing', 'inside-bar', 'doji', 'hammer', 'shooting-star']),
  bias: z.enum(['bullish', 'bearish']),
  time: z.number(),
  price: z.number(),
});

const _swingPivotSchema = z.object({
  type: z.enum(['high', 'low']),
  time: z.number(),
  price: z.number(),
});

const smcOverlaySchema = z.object({
  kind: z.literal('smc'),
  orderBlocks: z.array(_zoneSchema).optional(),
  fvg: z.array(_fvgSchema).optional(),
  liquidity: z.array(_liquiditySchema).optional(),
  bos: _structurePointSchema.nullable().optional(),
  choch: _structurePointSchema.nullable().optional(),
  keyLevels: z
    .object({
      recentHigh: z.number(),
      recentLow: z.number(),
      swingHigh: z.number(),
      swingLow: z.number(),
    })
    .optional(),
});

const ictOverlaySchema = z.object({
  kind: z.literal('ict'),
  po3: z
    .array(
      z.object({
        phase: z.enum(['accumulation', 'manipulation', 'distribution']),
        startTime: z.number(),
        endTime: z.number(),
        topPrice: z.number().optional(),
        bottomPrice: z.number().optional(),
      })
    )
    .optional(),
  killzones: z
    .array(
      z.object({
        name: z.enum(['london', 'new-york-am', 'london-close', 'new-york-pm', 'asia']),
        startTime: z.number(),
        endTime: z.number(),
      })
    )
    .optional(),
  ifvg: z.array(_fvgSchema).optional(),
  ote: z
    .object({
      topPrice: z.number(),
      bottomPrice: z.number(),
      time: z.number().optional(),
    })
    .nullable()
    .optional(),
  mss: _structurePointSchema.nullable().optional(),
});

const priceActionOverlaySchema = z.object({
  kind: z.literal('price-action'),
  pinBars: z.array(_candlePatternSchema).optional(),
  engulfings: z.array(_candlePatternSchema).optional(),
  pivots: z.array(_swingPivotSchema).optional(),
  supply: z.array(_zoneSchema).optional(),
  demand: z.array(_zoneSchema).optional(),
});

const fibonacciOverlaySchema = z.object({
  kind: z.literal('fibonacci'),
  anchor: z
    .object({
      startTime: z.number(),
      startPrice: z.number(),
      endTime: z.number(),
      endPrice: z.number(),
      direction: z.enum(['bullish', 'bearish']),
    })
    .nullable()
    .optional(),
  levels: z.array(z.object({ ratio: z.number(), price: z.number() })).optional(),
  extensions: z.array(z.object({ ratio: z.number(), price: z.number() })).optional(),
  goldenZone: z
    .object({ lowPrice: z.number(), highPrice: z.number() })
    .nullable()
    .optional(),
});

export const methodologyOverlaySchema = z.discriminatedUnion('kind', [
  smcOverlaySchema,
  ictOverlaySchema,
  priceActionOverlaySchema,
  fibonacciOverlaySchema,
]);

export const alertCreateSchema = z.object({
  type: z.enum(['price', 'setup', 'target_hit', 'stop_hit', 'trend_change']),
  symbol: z.string().min(1),
  condition: z.enum(['gte', 'lte', 'eq', 'cross_up', 'cross_down']),
  price: z.number().positive(),
  message: z.string().max(500).optional(),
  channels: z.array(z.enum(['telegram', 'email', 'whatsapp', 'push'])).min(1),
  expiresAt: z.string().datetime().optional(),
});

export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format a number as price with specified precision
 */
export function formatPrice(value: number, precision: number = 2): string {
  return value.toFixed(precision);
}

/**
 * Format a number as percentage
 */
export function formatPercent(value: number, precision: number = 2): string {
  return `${(value * 100).toFixed(precision)}%`;
}

/**
 * Format a number with thousand separators
 */
export function formatNumber(value: number, locale: string = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Format currency based on locale
 */
export function formatCurrency(
  value: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Format date for display.
 *
 * Accepts Date, epoch number, ISO/parseable string, or null/undefined.
 * Returns the placeholder (default '—') for missing or unparseable values
 * instead of throwing `RangeError: Invalid time value`.
 */
export function formatDate(
  date: Date | number | string | null | undefined,
  locale: string = 'en-US',
  options?: Intl.DateTimeFormatOptions & { invalidPlaceholder?: string }
): string {
  const { invalidPlaceholder = '—', ...formatOptions } = options ?? {};
  if (date === null || date === undefined || date === '') return invalidPlaceholder;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return invalidPlaceholder;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...formatOptions,
  }).format(d);
}

/**
 * Format relative time (e.g., "2 hours ago").
 *
 * Accepts the same inputs as `formatDate` and returns the placeholder
 * (default '—') for missing or unparseable values.
 */
export function formatRelativeTime(
  date: Date | number | string | null | undefined,
  locale: string = 'en-US',
  invalidPlaceholder: string = '—'
): string {
  if (date === null || date === undefined || date === '') return invalidPlaceholder;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return invalidPlaceholder;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffDays > 0) return rtf.format(-diffDays, 'day');
  if (diffHours > 0) return rtf.format(-diffHours, 'hour');
  if (diffMins > 0) return rtf.format(-diffMins, 'minute');
  return rtf.format(-diffSecs, 'second');
}

// ============================================================================
// Calculation Utilities
// ============================================================================

/**
 * Calculate total number of pages for pagination.
 *
 * Always returns at least 1, so empty results still show page 1.
 */
export function calculateTotalPages(total: number, pageSize: number): number {
  if (total <= 0 || pageSize <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * Calculate risk/reward ratio
 */
export function calculateRiskReward(
  entry: number,
  stopLoss: number,
  target: number
): number {
  const risk = Math.abs(entry - stopLoss);
  const reward = Math.abs(target - entry);

  if (risk === 0) return 0;
  return reward / risk;
}

/**
 * Calculate position risk percentage
 */
export function calculateRiskPercent(
  entry: number,
  stopLoss: number
): number {
  return Math.abs((entry - stopLoss) / entry);
}

/**
 * Calculate pip value for forex
 */
export function calculatePips(
  price1: number,
  price2: number,
  precision: number = 5
): number {
  const diff = Math.abs(price1 - price2);
  const pipSize = precision === 3 ? 0.01 : 0.0001;
  return diff / pipSize;
}

/**
 * Get candle direction
 */
export function getCandleDirection(candle: Candle): 'bullish' | 'bearish' | 'doji' {
  const bodySize = Math.abs(candle.close - candle.open);
  const totalRange = candle.high - candle.low;

  if (totalRange === 0 || bodySize / totalRange < 0.1) {
    return 'doji';
  }

  return candle.close > candle.open ? 'bullish' : 'bearish';
}

/**
 * Calculate Simple Moving Average
 */
export function calculateSMA(candles: Candle[], period: number): number[] {
  if (candles.length < period) return [];

  const sma: number[] = [];
  for (let i = period - 1; i < candles.length; i++) {
    const sum = candles
      .slice(i - period + 1, i + 1)
      .reduce((acc, c) => acc + c.close, 0);
    sma.push(sum / period);
  }

  return sma;
}

// ============================================================================
// Time Utilities
// ============================================================================

/**
 * Convert timeframe to seconds
 */
export function timeframeToSeconds(timeframe: Timeframe): number {
  return TIMEFRAMES[timeframe].seconds;
}

/**
 * Get start time for a given number of candles
 */
export function getStartTime(
  timeframe: Timeframe,
  candleCount: number,
  fromTime: Date = new Date()
): Date {
  const seconds = timeframeToSeconds(timeframe) * candleCount;
  return new Date(fromTime.getTime() - seconds * 1000);
}

/**
 * Round timestamp to candle boundary
 */
export function roundToCandleBoundary(
  timestamp: number,
  timeframe: Timeframe
): number {
  const seconds = timeframeToSeconds(timeframe);
  return Math.floor(timestamp / (seconds * 1000)) * seconds * 1000;
}

// ============================================================================
// Subscription Utilities
// ============================================================================

/**
 * Check if user can perform analysis based on plan
 */
export function canPerformAnalysis(
  plan: SubscriptionPlan,
  dailyCount: number
): boolean {
  const limit = SUBSCRIPTION_PLANS[plan].analysesPerDay;
  return limit === -1 || dailyCount < limit;
}

/**
 * Check if timeframe is available for plan
 */
export function isTimeframeAvailable(
  plan: SubscriptionPlan,
  timeframe: Timeframe
): boolean {
  return SUBSCRIPTION_PLANS[plan].timeframes.includes(timeframe);
}

/**
 * Get remaining analyses for the day
 */
export function getRemainingAnalyses(
  plan: SubscriptionPlan,
  dailyCount: number
): number {
  const limit = SUBSCRIPTION_PLANS[plan].analysesPerDay;
  if (limit === -1) return -1; // -1 signals unlimited (matches input convention); avoids JSON.stringify(Infinity) → null
  return Math.max(0, limit - dailyCount);
}

/**
 * Check if methodology is available for plan
 */
export function isMethodologyAvailable(
  plan: SubscriptionPlan,
  methodology: string
): boolean {
  return SUBSCRIPTION_PLANS[plan].methodologies.includes(methodology);
}

/**
 * Check if user can add a new asset (within asset limit)
 */
export function isAssetWithinLimit(
  plan: SubscriptionPlan,
  currentDistinctAssets: number,
  symbolAlreadyUsed: boolean
): boolean {
  const limit = SUBSCRIPTION_PLANS[plan].assetsLimit;
  if (limit === null) return true;
  if (symbolAlreadyUsed) return true;
  return currentDistinctAssets < limit;
}

/**
 * Check if user can create more alerts
 */
export function canCreateAlert(
  plan: SubscriptionPlan,
  activeAlertCount: number
): boolean {
  const limit = SUBSCRIPTION_PLANS[plan].alertsLimit;
  if (limit === -1) return true;
  return activeAlertCount < limit;
}

// ============================================================================
// String Utilities
// ============================================================================

/**
 * Generate a random string
 */
export function generateRandomString(length: number = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Slugify a string
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Truncate string with ellipsis
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
}

// ============================================================================
// Type Guards
// ============================================================================

export function isValidTimeframe(value: string): value is Timeframe {
  return Object.keys(TIMEFRAMES).includes(value);
}

export function isValidPlan(value: string): value is SubscriptionPlan {
  return Object.keys(SUBSCRIPTION_PLANS).includes(value);
}

// ============================================================================
// User Custom Prompts (Pro+ feature)
// ============================================================================

/**
 * DB-side methodology values. Note `price_action` (underscore) — this matches
 * the AnalysisMethodology Prisma enum, not the frontend kebab-case type
 * (`price-action`). Custom prompts are stored using the DB form so the worker
 * can match by methodology directly. `null`/undefined means "applies to every
 * methodology".
 */
export const userPromptMethodologySchema = z.enum([
  'smc',
  'ict',
  'price_action',
  'fibonacci',
]);

export const userPromptCreateSchema = z.object({
  name: z.string().min(1, 'validation.name.required').max(100, 'validation.name.maxLength'),
  content: z.string().min(1, 'validation.content.required').max(3000, 'validation.content.maxLength'),
  methodology: userPromptMethodologySchema.optional().nullable(),
  isDefault: z.boolean().optional(),
});

export const userPromptUpdateSchema = userPromptCreateSchema.partial();

/**
 * Whether the given plan is allowed to use the Custom Prompts feature.
 * Equivalent to `customPromptsLimit > 0` or `customPromptsLimit === null`.
 */
export function canUseCustomPrompts(plan: SubscriptionPlan): boolean {
  const limit = SUBSCRIPTION_PLANS[plan].customPromptsLimit;
  return limit === null || limit > 0;
}

/**
 * Whether the user can create another custom prompt without hitting the plan
 * cap. Returns true for unlimited (`limit === null`).
 */
export function canCreateCustomPrompt(
  plan: SubscriptionPlan,
  currentCount: number,
): boolean {
  const limit = SUBSCRIPTION_PLANS[plan].customPromptsLimit;
  if (limit === null) return true;
  return currentCount < limit;
}

// ============================================================================
// Journal Validation Schemas
// ============================================================================

export const journalAccountSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  broker: z.string().max(100).optional().nullable(),
  type: z.enum(['LIVE', 'PAPER', 'BACKTEST']).optional(),
  initialBalance: z.number().min(0).optional(),
  currency: z.string().length(3, 'Currency must be a 3-letter code').optional(),
});

export const journalAccountUpdateSchema = journalAccountSchema.partial();

export const tradeMetadataSchema = z.object({
  setupTag: z.string().max(100).optional().nullable(),
  mistakeTags: z.array(z.string().max(50)).max(20).default([]),
  emotionPre: z.enum(['CONFIDENT', 'ANXIOUS', 'NEUTRAL', 'EXCITED', 'FEARFUL', 'REVENGE']).optional().nullable(),
  emotionPost: z.enum(['SATISFIED', 'REGRETFUL', 'NEUTRAL', 'FRUSTRATED', 'RELIEVED']).optional().nullable(),
  conviction: z.number().int().min(1).max(5).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
  rating: z.number().int().min(1).max(5).optional().nullable(),
});

const SORTABLE_TRADE_COLUMNS = [
  'entryDate',
  'symbol',
  'direction',
  'entryPrice',
  'exitPrice',
  'quantity',
  'netPnl',
  'result',
] as const;

// Accepts ISO 8601 datetime ('2026-01-15T10:00:00Z') or date-only ('2026-01-15')
const dateOrDatetimeSchema = z.union([
  z.string().datetime(),
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'validation.date.formatYMD'),
]);

/** @internal Base schema — also used by syncTradeSchema. Must stay as z.object(), do not wrap with .refine()/.transform(). */
export const journalTradeCreateBaseSchema = z.object({
  accountId: z.string().uuid().optional(),
  playbookId: z.string().uuid().optional().nullable(),
  analysisId: z.string().uuid().optional().nullable(),
  source: z.enum(['MANUAL', 'CSV', 'API']).default('MANUAL'),
  instrumentType: z.enum(['STOCK', 'OPTION', 'FUTURE', 'FOREX', 'CRYPTO']),
  symbol: z.string().min(1).max(20),
  direction: z.enum(['LONG', 'SHORT']),
  entryPrice: z.number().positive('validation.entryPrice.positive').max(TRADE_LIMITS.PRICE_MAX, 'validation.entryPrice.max'),
  exitPrice: z.number().positive().max(TRADE_LIMITS.PRICE_MAX, 'validation.exitPrice.max').optional().nullable(),
  stopLoss: z.number().min(0).max(TRADE_LIMITS.PRICE_MAX, 'validation.stopLoss.max').optional().nullable(),
  takeProfit: z.number().min(0).max(TRADE_LIMITS.PRICE_MAX, 'validation.takeProfit.max').optional().nullable(),
  quantity: z.number().positive().max(TRADE_LIMITS.QUANTITY_MAX, 'validation.quantity.max').optional().nullable(),
  fees: z.number().min(0).max(TRADE_LIMITS.FEES_MAX, 'validation.fees.max').default(0),
  /** Optional direct net P&L override. When provided, bypasses price-based computation. */
  netPnl: z.number().min(-TRADE_LIMITS.PNL_MAX, 'validation.netPnl.min').max(TRADE_LIMITS.PNL_MAX, 'validation.netPnl.max').optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
  tags: z.array(z.string().max(50)).max(20).default([]),
  timeframe: z.string().max(10).optional().nullable(),
  entryDate: z.string().datetime(),
  exitDate: z.string().datetime().optional().nullable(),
});

/**
 * Returns true when the SL/Entry/TP ordering is valid for the given direction,
 * or when any of the three values is missing (field is optional — we only
 * enforce the constraint when all three are present).
 *
 * LONG:  SL < Entry < TP
 * SHORT: TP < Entry < SL
 */
export function isValidSlEntryTpOrdering(
  direction: 'LONG' | 'SHORT',
  entryPrice: number | null | undefined,
  stopLoss: number | null | undefined,
  takeProfit: number | null | undefined,
): boolean {
  // Skip when any anchor is absent — partial saves are allowed.
  if (entryPrice == null || stopLoss == null || takeProfit == null) return true;
  if (direction === 'LONG') {
    return stopLoss < entryPrice && entryPrice < takeProfit;
  }
  // SHORT
  return takeProfit < entryPrice && entryPrice < stopLoss;
}

export const journalTradeCreateSchema = journalTradeCreateBaseSchema
  .refine(
    (d) => !d.exitDate || new Date(d.exitDate) >= new Date(d.entryDate),
    { message: 'validation.exitDate.afterEntry', path: ['exitDate'] },
  )
  .refine(
    (d) => isValidSlEntryTpOrdering(d.direction, d.entryPrice, d.stopLoss, d.takeProfit),
    (d) => ({
      message:
        d.direction === 'LONG'
          ? 'validation.slEntryTp.longOrder'
          : 'validation.slEntryTp.shortOrder',
      path: ['entryPrice'],
    }),
  );

export const journalTradeUpdateSchema = journalTradeCreateBaseSchema
  .partial()
  .refine(
    (d) => !d.exitDate || !d.entryDate || new Date(d.exitDate) >= new Date(d.entryDate),
    { message: 'validation.exitDate.afterEntry', path: ['exitDate'] },
  )
  .refine(
    (d) =>
      // Only enforce when direction is present (update may not include it)
      !d.direction ||
      isValidSlEntryTpOrdering(d.direction, d.entryPrice, d.stopLoss, d.takeProfit),
    (d) => ({
      message:
        d.direction === 'SHORT'
          ? 'validation.slEntryTp.shortOrder'
          : 'validation.slEntryTp.longOrder',
      path: ['entryPrice'],
    }),
  );

export const journalPlaybookCreateSchema = z.object({
  name: z.string().min(1, 'validation.name.required').max(100, 'validation.name.maxLength'),
  description: z.string().max(500, 'validation.description.maxLength').optional().nullable(),
  rules: z.string().min(1, 'validation.rules.required').max(10000),
});

export const journalPlaybookUpdateSchema = journalPlaybookCreateSchema.partial();

// ============================================================================
// Sync Validation Schemas (MT5 / external platform trade sync)
// ============================================================================

// NOTE: syncTradeSchema intentionally omits the SL/Entry/TP directional ordering
// guard. MT5 EA data is considered trusted at ingestion time; the EA enforces
// order placement rules upstream. Adding the refine here would reject valid
// historical trades that arrived without one of the three price levels.
export const syncTradeSchema = journalTradeCreateBaseSchema
  .omit({ source: true, accountId: true, playbookId: true, analysisId: true })
  .extend({
    externalId: z.string().min(1),
    exitPrice: z.number().positive().max(TRADE_LIMITS.PRICE_MAX, 'validation.exitPrice.max'),
    exitDate: z.string().datetime(),
    quantity: z.number().positive().max(TRADE_LIMITS.QUANTITY_MAX, 'validation.quantity.max'),
    grossPnl: z.number().optional(),
    netPnl: z.number().optional(),
  })
  .refine(
    (d) => new Date(d.exitDate) >= new Date(d.entryDate),
    { message: 'Exit date must be after entry date', path: ['exitDate'] }
  );

export const syncBatchSchema = z.object({
  platform: z.enum(['mt5']),
  trades: z.array(syncTradeSchema).min(1).max(200),
});

// ============================================================================
// Open Positions Sync Schema (MT5 periodic snapshot)
// ============================================================================

export const openPositionSchema = z.object({
  brokerTicket: z.string().min(1),
  symbol: z.string().min(1).max(30),
  side: z.enum(['LONG', 'SHORT']),
  volume: z.number().positive(),
  entryPrice: z.number().positive(),
  currentPrice: z.number().positive().optional(),
  stopLoss: z.number().nullable().optional(),
  takeProfit: z.number().nullable().optional(),
  openedAt: z.string().datetime(),
  unrealizedPnL: z.number().optional(),
});

export const syncOpenPositionsSchema = z.object({
  accountId: z.string().optional(),
  capturedAt: z.string().datetime(),
  positions: z.array(openPositionSchema),
});

export type SyncOpenPositionsInput = z.infer<typeof syncOpenPositionsSchema>;

export const journalTradeFiltersSchema = z.object({
  accountId: z.string().uuid().optional(),
  symbol: z.string().optional(),
  direction: z.enum(['LONG', 'SHORT']).optional(),
  // Accepts a single value ('WIN') or a comma-separated list ('WIN,LOSS,BREAKEVEN')
  // for multi-value filters such as the "Closed" shorthand. Each token must be a
  // valid TradeResult enum value. Parsing into an array is done in buildTradeWhere.
  result: z
    .string()
    .regex(
      /^(WIN|LOSS|BREAKEVEN|OPEN)(,(WIN|LOSS|BREAKEVEN|OPEN))*$/
    )
    .optional(),
  instrumentType: z.enum(['STOCK', 'OPTION', 'FUTURE', 'FOREX', 'CRYPTO']).optional(),
  playbookId: z.string().uuid().optional(),
  startDate: dateOrDatetimeSchema.optional(),
  endDate: dateOrDatetimeSchema.optional(),
});

export const journalTradeSortSchema = z.object({
  sortBy: z.enum(SORTABLE_TRADE_COLUMNS).default('entryDate'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

// ============================================================================
// Trade P&L Computation
// ============================================================================

export interface ComputeTradeFieldsInput {
  entryPrice: number;
  exitPrice?: number | null;
  quantity?: number | null;
  fees?: number | null;
  direction: 'LONG' | 'SHORT';
}

export interface ComputeTradeFieldsResult {
  grossPnl: number;
  netPnl: number;
  result: 'WIN' | 'LOSS' | 'BREAKEVEN' | 'OPEN';
}

export function computeTradeFields(input: ComputeTradeFieldsInput): ComputeTradeFieldsResult {
  const { entryPrice, exitPrice, quantity, fees, direction } = input;

  if (exitPrice == null) {
    return { grossPnl: 0, netPnl: 0, result: 'OPEN' };
  }

  const qty = quantity ?? 1;
  const feeAmount = fees ?? 0;
  const multiplier = direction === 'SHORT' ? -1 : 1;

  const grossPnl = (exitPrice - entryPrice) * qty * multiplier;
  const netPnl = grossPnl - feeAmount;

  let result: 'WIN' | 'LOSS' | 'BREAKEVEN';
  if (netPnl > 0) result = 'WIN';
  else if (netPnl < 0) result = 'LOSS';
  else result = 'BREAKEVEN';

  return { grossPnl, netPnl, result };
}

// ============================================================================
// Analytics (re-exported from analytics.ts)
// ============================================================================
export * from './analytics.js';

// Trade Event Schemas
export * from './tradeEvent.js';
export * from './tradeCommand.js';

// Signal Schemas
export * from './signal.js';

// ============================================================================
// Market data utilities
// ============================================================================

// Symbol mapping (internal ↔ provider)
export * from './symbolMap.js';

// P&L pip multiplier helper
export * from './pipMultiplier.js';

// TwelveData shared client
export * from './twelvedata-client.js';

// Forex market session helpers
export * from './marketHours.js';

// ============================================================================
// Multiplexed WebSocket wire protocol
// ============================================================================
export * from './wsProtocol.js';

// ============================================================================
// SMC (Smart Money Concepts) precomputed structure + zone utilities
// ============================================================================
export * from './smcStructure.js';
export * from './smcZones.js';

// ============================================================================
// Candlestick pattern detection (pure, no I/O)
// ============================================================================
export * from './candlePatterns.js';

// ============================================================================
// Fibonacci level precomputation (pure, no I/O)
// ============================================================================
export * from './fibonacci.js';

// ============================================================================
// Backtest pure helpers (pip factors, spread, SMC thresholds, evaluator, signal generators)
// ============================================================================
export * from './backtestHelpers.js';
